import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api, { resolveImg, formatPrice } from "@/lib/api";

export const SearchBar = ({ className = "" }) => {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const loadedRef = useRef(false);
  const navigate = useNavigate();

  // Fetch the catalog only when the visitor actually uses search,
  // so pages don't preload every product up front.
  const ensureLoaded = () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [query, products]);

  const priceLabel = (p) => {
    const vp = (p.variants || []).map((v) => v.price).filter((x) => x != null);
    return vp.length ? `From ${formatPrice(Math.min(...vp))}` : formatPrice(p.price);
  };

  const go = (id) => {
    setOpen(false);
    setQuery("");
    navigate(`/product/${id}`);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid="search-bar">
      <div className="flex items-center gap-2 border-b border-stone-300 py-1.5 transition-colors duration-300 focus-within:border-wine">
        <Search size={16} className="shrink-0 text-stone-400" strokeWidth={1.5} />
        <input
          data-testid="search-input"
          value={query}
          onChange={(e) => { ensureLoaded(); setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { ensureLoaded(); query && setOpen(true); }}
          placeholder="Search jewelry…"
          className="w-full bg-transparent text-sm text-stone-800 outline-none placeholder:text-stone-400"
        />
        {query && (
          <button data-testid="search-clear" onClick={() => { setQuery(""); setOpen(false); }} className="shrink-0 text-stone-400 hover:text-wine">
            <X size={14} />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div
          className="absolute right-0 top-full z-[60] mt-2 w-80 max-w-[90vw] max-h-[70vh] overflow-auto border border-stone-200/70 bg-white/85 shadow-xl backdrop-blur-xl"
          data-testid="search-results"
        >
          {results.length === 0 ? (
            <div className="px-4 py-4 text-sm text-stone-400" data-testid="search-no-results">No matches for “{query}”.</div>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                data-testid={`search-result-${p.id}`}
                onClick={() => go(p.id)}
                className="flex w-full items-center gap-3 border-b border-stone-100 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-secondary"
              >
                <img src={resolveImg(p.images?.[0])} alt="" className="h-11 w-11 shrink-0 object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-stone-900">{p.name}</div>
                  <div className="text-xs capitalize text-stone-400">{p.category}</div>
                </div>
                <div className="whitespace-nowrap text-sm font-bold text-stone-900">{priceLabel(p)}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
